import { useBreakpointV2 } from "@tm/ui-widgets";
import React, { useCallback } from "react";

import { ImageComponent, ReactPortalComp } from "../../../../../components";
import CommonModalDweb from "../../../../../components/CommonModalDweb/CommonModalDweb";
import FormattedText, {
  getPlainText,
} from "../../../../../components/FormattedText/FormattedText";
import cancelGreyBg from "../../../../../icons/cancelGreyBg.svg";
import type {
  ClaimDetailDocument,
  ClaimDetailGroup,
  ClaimDetailSheet,
  ClaimDetailStatusInfo,
  TextNode,
} from "../../../types";
import {
  CloseButton,
  DetailInfoLabel,
  DetailInfoList,
  DetailInfoRow,
  DetailInfoValue,
  Divider,
  DocumentRow,
  DocumentsValue,
  Overlay,
  SectionsList,
  SheetHeader,
  SheetPanel,
  SheetTitle,
  StatusInfoCard,
  StatusInfoDescription,
  StatusInfoFootnote,
  StatusInfoTitle,
} from "./ClaimDetailModal.style";

interface ClaimDetailModalProps {
  data: ClaimDetailSheet;
  onClose: () => void;
}

function hasText(node?: TextNode): boolean {
  return Boolean(getPlainText(node).trim());
}

function groupHasItems(group?: ClaimDetailGroup): boolean {
  return (group?.items ?? []).some(
    (it) => hasText(it?.label) || hasText(it?.value)
  );
}

function StatusSection({ status }: { status?: ClaimDetailStatusInfo }) {
  if (!status) return null;
  const showTitle = hasText(status.title);
  const showDesc = hasText(status.description);
  const showFootnote = hasText(status.footnote);
  if (!showTitle && !showDesc && !showFootnote) return null;
  return (
    <StatusInfoCard>
      {showTitle ? (
        <StatusInfoTitle>
          <FormattedText value={status.title} />
        </StatusInfoTitle>
      ) : null}
      {showDesc ? (
        <StatusInfoDescription>
          <FormattedText value={status.description} />
        </StatusInfoDescription>
      ) : null}
      {showFootnote ? (
        <StatusInfoFootnote>
          <FormattedText value={status.footnote} />
        </StatusInfoFootnote>
      ) : null}
    </StatusInfoCard>
  );
}

function InfoGroup({ group }: { group?: ClaimDetailGroup }) {
  const items = (group?.items ?? []).filter(
    (it) => hasText(it?.label) || hasText(it?.value)
  );
  if (items.length === 0) return null;
  return (
    <DetailInfoList>
      {items.map((item, idx) => (
        <DetailInfoRow key={item.key ?? idx}>
          {hasText(item.label) ? (
            <DetailInfoLabel>
              <FormattedText value={item.label} />
            </DetailInfoLabel>
          ) : (
            <DetailInfoLabel />
          )}
          {hasText(item.value) ? (
            <DetailInfoValue>
              <FormattedText value={item.value} />
            </DetailInfoValue>
          ) : null}
        </DetailInfoRow>
      ))}
    </DetailInfoList>
  );
}

function DocumentsSection({
  label,
  documents,
}: {
  label?: TextNode;
  documents?: ClaimDetailDocument[];
}) {
  const docs = (documents ?? []).filter(
    (d) => d?.fileName || hasText(d?.title)
  );
  if (docs.length === 0) return null;
  return (
    <DetailInfoRow>
      {hasText(label) ? (
        <DetailInfoLabel>
          <FormattedText value={label} />
        </DetailInfoLabel>
      ) : null}
      <DocumentsValue>
        {docs.map((doc, idx) => (
          <DocumentRow key={doc.documentId ?? doc.key ?? doc.fileName ?? idx}>
            {hasText(doc.title) ? (
              <FormattedText value={doc.title} />
            ) : (
              doc.fileName
            )}
          </DocumentRow>
        ))}
      </DocumentsValue>
    </DetailInfoRow>
  );
}

export function ClaimDetailModal({ data, onClose }: ClaimDetailModalProps) {
  const device = useBreakpointV2();
  const title = getPlainText(data?.title).trim();

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  const hasStatus =
    !!data?.status &&
    (hasText(data.status.title) ||
      hasText(data.status.description) ||
      hasText(data.status.footnote));
  const hasClaimInfo = groupHasItems(data?.claimInfo);
  const hasFormData = groupHasItems(data?.formData);
  const hasDocs = (data?.documents?.items ?? []).some(
    (d) => d?.fileName || hasText(d?.title)
  );

  if (!hasStatus && !hasClaimInfo && !hasFormData && !hasDocs) return null;

  const showDivAfterClaimInfo = hasClaimInfo && (hasFormData || hasDocs);
  const showDivAfterFormData = hasFormData && hasDocs;

  const body = (
    <SectionsList>
      {hasStatus ? <StatusSection status={data?.status} /> : null}
      {hasClaimInfo ? <InfoGroup group={data?.claimInfo} /> : null}
      {showDivAfterClaimInfo ? <Divider /> : null}
      {hasFormData ? <InfoGroup group={data?.formData} /> : null}
      {showDivAfterFormData ? <Divider /> : null}
      {hasDocs ? (
        <DocumentsSection
          label={data?.documents?.label}
          documents={data?.documents?.items}
        />
      ) : null}
    </SectionsList>
  );

  if (device === "sm") {
    return (
      <ReactPortalComp>
        <Overlay onClick={handleOverlayClick}>
          <SheetPanel
            role="dialog"
            aria-modal="true"
            aria-label={title}
            $maxHeight="82vh"
            $padding="22px 20px 20px"
          >
            <SheetHeader>
              <SheetTitle>{title}</SheetTitle>
              <CloseButton onClick={onClose} aria-label="Close">
                <ImageComponent
                  src={cancelGreyBg.src}
                  width="28px"
                  height="28px"
                  alt="Close"
                />
              </CloseButton>
            </SheetHeader>
            {body}
          </SheetPanel>
        </Overlay>
      </ReactPortalComp>
    );
  }

  return (
    <CommonModalDweb
      customTitle={title}
      handleClick={onClose}
      customWidth="460px"
      customHeight="auto"
    >
      {body}
    </CommonModalDweb>
  );
}
